import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { JWTManager } from '../utils/jwt';
import { auth } from '../config/firebase';
import { onAuthStateChanged, User as FirebaseUser, signOut as firebaseSignOut } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { CacheManager } from '../utils/cacheManager';
import { authInterceptor } from '../services/authInterceptor';

interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  userEmail: string | null;
  userId: string | number | null;
  // Firebase-specific fields
  firebaseUser: FirebaseUser | null;
  authMethod: 'jwt' | 'firebase' | null;
}

interface AuthContextType extends AuthState {
  isInitialized: boolean;
  login: (token: string) => void;
  logout: () => void;
  // Firebase-specific methods
  loginWithFirebase: () => void;
  logoutFromFirebase: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isFirebaseLoaded, setIsFirebaseLoaded] = useState(false);

  const [authState, setAuthState] = useState<AuthState>(() => {
    const isValid = JWTManager.isTokenValid();
    return {
      isAuthenticated: isValid,
      username: isValid ? JWTManager.getUsername() : null,
      userEmail: isValid ? JWTManager.getUserEmail() : null,
      userId: isValid ? JWTManager.getUserId() : null,
      firebaseUser: null,
      authMethod: isValid ? 'jwt' : null,
    };
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Single function to update all auth state at once
  const updateAuthState = useCallback(() => {
    const isJwtValid = JWTManager.isTokenValid();
    const isFirebaseValid = Boolean(firebaseUser);
    
    // Determine which authentication method is active
    const isAuthenticated = isJwtValid || isFirebaseValid;
    const authMethod = isJwtValid ? 'jwt' : isFirebaseValid ? 'firebase' : null;

    const newState = {
      isAuthenticated,
      username: isJwtValid ? JWTManager.getUsername() : (firebaseUser?.displayName || firebaseUser?.email?.split('@')[0] || null),
      userEmail: isJwtValid ? JWTManager.getUserEmail() : (firebaseUser?.email || null),
      userId: isJwtValid ? JWTManager.getUserId() : (firebaseUser?.uid || null),
      firebaseUser: isFirebaseValid ? firebaseUser : null,
      authMethod: authMethod as 'jwt' | 'firebase' | null,
    };

    setAuthState(newState);
  }, [firebaseUser]);

  const logout = async () => {
    // Always try to logout from Firebase if user is signed in
    if (firebaseUser) {
      try {
        if (Capacitor.isNativePlatform()) {
          // Use Capacitor Firebase Auth for native platforms
          await FirebaseAuthentication.signOut();
        } else {
          // Use Firebase Web SDK for web
          const webAuth = auth || (await import('../config/firebase')).auth;
          if (webAuth) {
            await firebaseSignOut(webAuth);
          }
        }
      } catch (error) {
        console.error('Firebase logout error');
      }
    }

    // Always clear JWT token (in case it exists)
    JWTManager.removeToken();
    
    // Clear all cache data to leave app as if user was never there
    CacheManager.clearAllCache();
    
    // Clear any localStorage items that might be set during authentication
    localStorage.removeItem('nuroleap_user_id');
    localStorage.removeItem('nuroleap_last_login');
    
    // Reset auth state
    setAuthState({
      isAuthenticated: false,
      username: null,
      userEmail: null,
      userId: null,
      firebaseUser: null,
      authMethod: null,
    });
    setFirebaseUser(null);
  };

  // Set up Firebase auth state listener (solo en web)
  useEffect(() => {
    // En móviles, usar el plugin nativo para escuchar cambios de autenticación
    if (Capacitor.isNativePlatform()) {
      // Check for existing Firebase user on native platforms
      const checkNativeFirebaseUser = async () => {
        try {
          const currentUser = await FirebaseAuthentication.getCurrentUser();
          if (currentUser?.user) {
            // Create a mock Firebase User object for native platforms
            // This allows the auth state to recognize Firebase authentication
            const mockFirebaseUser = {
              uid: currentUser.user.uid,
              email: currentUser.user.email || null,
              displayName: currentUser.user.displayName || null,
              emailVerified: currentUser.user.emailVerified || false,
            } as FirebaseUser;
            setFirebaseUser(mockFirebaseUser);
          } else {
            setFirebaseUser(null);
          }
        } catch (error) {
          setFirebaseUser(null);
        } finally {
          setIsFirebaseLoaded(true);
        }
      };
      
      checkNativeFirebaseUser();
      
      // Listen for auth success events from login flow
      const handleAuthSuccess = async () => {
        // Re-check for current user
        try {
          const currentUser = await FirebaseAuthentication.getCurrentUser();
          if (currentUser?.user) {
            const mockFirebaseUser = {
              uid: currentUser.user.uid,
              email: currentUser.user.email || null,
              displayName: currentUser.user.displayName || null,
              emailVerified: currentUser.user.emailVerified || false,
            } as FirebaseUser;
            setFirebaseUser(mockFirebaseUser);
          }
        } catch (error) {
          console.warn('Could not get Firebase user after auth success');
        }
      };
      
      window.addEventListener('firebase-auth-success', handleAuthSuccess);
      
      return () => {
        window.removeEventListener('firebase-auth-success', handleAuthSuccess);
      };
    }

    // Solo en web: usar Firebase Web SDK
    let unsubscribe: (() => void) | null = null;
    
    const setupAuthListener = async () => {
      const webAuth = auth || (await import('../config/firebase')).auth;
      if (webAuth) {
        unsubscribe = onAuthStateChanged(webAuth, (user) => {
          setFirebaseUser(user);
          setIsFirebaseLoaded(true);
        });
      } else {
        setIsFirebaseLoaded(true);
      }
    };

    setupAuthListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Initial auth check and setup - wait for Firebase to load
  useEffect(() => {
    if (!isFirebaseLoaded) {
      // Still waiting for Firebase to load - don't initialize yet
      return;
    }
    
    // Firebase has finished loading, now we can safely check auth state
    updateAuthState();
    setIsInitialized(true);
  }, [isFirebaseLoaded, updateAuthState]);

  // Set up the authentication interceptor with logout callback (separate effect)
  useEffect(() => {
    authInterceptor.setLogoutCallback(() => {
      logout();
    });
  }, []); // Remove logout dependency to prevent loops

  // Watch for Firebase authentication changes
  useEffect(() => {
    if (!isInitialized) return;
    updateAuthState();
  }, [firebaseUser, isInitialized, updateAuthState]);

  const login = (token: string) => {
    JWTManager.setToken(token);
    updateAuthState();
  };

  const loginWithFirebase = () => {
    // Firebase authentication is handled by the login components
    // This method can be used for programmatic login if needed
    updateAuthState();
  };

  const logoutFromFirebase = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut();
      } else {
        const webAuth = auth || (await import('../config/firebase')).auth;
        if (webAuth) {
          await firebaseSignOut(webAuth);
        }
      }
    } catch (error) {
      console.error('Firebase logout error');
    }
    
    setAuthState({
      isAuthenticated: false,
      username: null,
      userEmail: null,
      userId: null,
      firebaseUser: null,
      authMethod: null,
    });
    setFirebaseUser(null);
    
    // Note: JWTManager.removeToken() is already called by CacheManager.clearAllCache()
    // but we can call it explicitly for extra safety
    JWTManager.removeToken();
  };

  return (
    <AuthContext.Provider value={{ 
      ...authState, 
      isInitialized, 
      login, 
      logout,
      loginWithFirebase,
      logoutFromFirebase
    }}>
      {children}
    </AuthContext.Provider>
  );
};